/**
 * Client-side ItineraryItem model with custom methods and computed properties
 */

import type { FlightOption } from '@elara/domain/models/schemas/flight-schemas';
import type { ItineraryItemEvent } from '@elara/domain/models/schemas/itinerary-item-event-schemas';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';
import type { Cost } from '@elara/domain/models/schemas/shared-schemas';
import type { CancellationDeadline } from '@elara/domain/payment-schedule';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';
import type { FlightOptionModel } from './FlightOptionModel.js';
import type { Hotel } from './HotelModel.js';
import type { Place } from './PlaceModel.js';
import type { RoomOptionModel } from './RoomOptionModel.js';
import type { RoomRateModel } from './RoomRateModel.js';

import { isAutomatedBookingChannel } from '@elara/domain/models/schemas/itinerary-item-schemas';
import { resolveCancellationDeadline } from '@elara/domain/payment-schedule';
import { ItineraryItemModelSpec } from '@elara/domain/models/ItineraryItemModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { formatDateRange, formatTimeInOriginalTimezone } from '#lib/date-utils.js';
import { expandSingleItineraryItem } from '#lib/itinerary-utils.js';
import { resolveRateFromMetadata } from '#lib/occupancy-utils.js';
import { describeCancellationPolicyFacts, formatCancellationDeadline } from '#lib/room-utils.js';
import { formatPrice, hydratedFromHandle, parseDocumentId,pluralize, tripIdFromPath } from '#lib/utils.js';

import { createClientFlightOptionModel } from './FlightOptionModel.js';
import { createClientHotelModel } from './HotelModel.js';
import { createClientPlaceModel } from './PlaceModel.js';
import { createClientRoomOptionModel } from './RoomOptionModel.js';

/**
 * ItineraryItem model for client-side usage
 * Provides reactive access to itinerary item data with computed properties and hydrated related entities.
 */
export function createClientItineraryItemModel(firestore: FirestoreContainer) {
  const FlightOptionModel = createClientFlightOptionModel(firestore);
  const RoomOptionModel = createClientRoomOptionModel(firestore);
  const PlaceModel = createClientPlaceModel(firestore);
  const HotelModel = createClientHotelModel(firestore);

  const model = createClientModel(firestore, ItineraryItemModelSpec, () => ({
    /** Display title for the itinerary item */
    get displayTitle(): string {
      return this.title || 'Untitled Item';
    },

    get formattedStartTime(): string | undefined {
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(this.startDateTime);
      return isDateOnly ? undefined : formatTimeInOriginalTimezone(this.startDateTime, 'h:mm a');
    },

    get formattedEndTime(): string | undefined {
      if (!this.endDateTime) return undefined;
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(this.endDateTime);
      return isDateOnly ? undefined : formatTimeInOriginalTimezone(this.endDateTime, 'h:mm a');
    },

    /** Formatted start date/time for display */
    get formattedStartDateTime(): string {
      if (!this.startDateTime) return 'No start time';

      try {
        const date = new Date(this.startDateTime);
        return date.toLocaleString();
      } catch {
        return this.startDateTime;
      }
    },

    /** Formatted end date/time for display */
    get formattedEndDateTime(): string {
      if (!this.endDateTime) return 'No end time';

      try {
        const date = new Date(this.endDateTime);
        return date.toLocaleString();
      } catch {
        return this.endDateTime;
      }
    },

    /** Duration between start and end times */
    get duration(): string {
      if (!this.startDateTime || !this.endDateTime) return 'Duration unknown';

      try {
        const start = new Date(this.startDateTime);
        const end = new Date(this.endDateTime);
        const diffMs = end.getTime() - start.getTime();

        if (diffMs < 0) return 'Invalid duration';

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else {
          return `${minutes}m`;
        }
      } catch {
        return 'Duration unknown';
      }
    },

    get formattedDateRange(): string {
      if (!this.startDateTime || !this.endDateTime) return '-';
      return formatDateRange(this.startDateTime, this.endDateTime);
    },

    /**
     * The confirmed stay dates frozen onto the booking projection at submit (ELARA-867) — what was
     * actually booked with Sabre — or undefined for external/manual bookings and bookings that predate
     * the freeze. Booked views should prefer these over the (mutable) item dates.
     */
    get confirmedStayDates(): { checkInDate: string; checkOutDate: string } | undefined {
      const checkInDate = this.booking?.checkInDate;
      const checkOutDate = this.booking?.checkOutDate;
      if (!checkInDate || !checkOutDate) return undefined;
      return { checkInDate, checkOutDate };
    },

    /**
     * Date range for booked views: the booking projection's confirmed dates when present (the
     * authoritative side — what Sabre booked), else the itinerary item's dates. Even if item/rate
     * drift ever recurs, the human reviewing a booking sees the truth (ELARA-867).
     */
    get formattedStayDateRange(): string {
      const confirmed = this.confirmedStayDates;
      if (confirmed) return formatDateRange(confirmed.checkInDate, confirmed.checkOutDate);
      return this.formattedDateRange;
    },

    /** Formatted cost for display */
    get formattedCost(): string {
      if (!this.cost) return '';

      const { amount, currency = 'USD' } = this.cost;
      return formatPrice(amount, currency);
    },

    get formattedCostDetail(): string | undefined {
      if (this.type === 'flight' && this.flightOption) {
        const { totalPassengers, outboundFlights, returnFlights, allFlights, roundTrip } = this.flightOption;
        const passengerLabel = `For ${totalPassengers} ${pluralize(totalPassengers, 'passenger')}`;
        // The price covers the whole itinerary (every leg), not just the one this card renders — say
        // so once there's more than one flight. A plain round trip (one leg each way) reads better
        // named than counted; any connection on either side falls back to a flight count instead.
        if (allFlights.length <= 1) return passengerLabel;
        const coverage =
          roundTrip && outboundFlights.length === 1 && returnFlights.length === 1
            ? 'outbound and return flights'
            : `all ${allFlights.length} flights`;
        return `${passengerLabel}. Total for ${coverage}.`;
      }
      if (this.type === 'room' && this.roomOption) return `For ${this.roomOption.nightsCount} ${pluralize(this.roomOption.nightsCount, 'night')}`;
      return undefined;
    },

    /** Check if item is a flight */
    get isFlight(): boolean {
      return this.type === 'flight';
    },

    /** Check if item is a room */
    get isRoom(): boolean {
      return this.type === 'room';
    },

    /** Check if item is a place */
    get isPlace(): boolean {
      return this.type === 'place';
    },

    /** Check if item has booking information */
    get hasBooking(): boolean {
      return !!this.booking;
    },

    /** Booking status for display */
    get bookingStatus(): string {
      if (!this.booking?.status) return 'No booking';
      return this.booking.status.charAt(0).toUpperCase() + this.booking.status.slice(1);
    },

    /** Check if item is booked */
    get isBooked(): boolean {
      return this.booking?.status === 'booked';
    },

    get isApproved(): boolean {
      return this.booking?.status === 'approved';
    },

    /** Check if item is planned */
    get isPlanned(): boolean {
      return this.booking?.status === 'planned' || !this.booking?.status;
    },

    /** Check if item is cancelled */
    get isCancelled(): boolean {
      return this.booking?.status === 'cancelled';
    },

    /** A live cancel that did not verify the segment is gone — terminal-cancel withheld (H2). */
    get isCancellationPending(): boolean {
      return this.booking?.status === 'cancellation_pending';
    },

    /**
     * True once the item carries actual booking details worth viewing/editing — a confirmation
     * number, a recorded special request, or an attachment. Deliberately excludes a bare booking
     * record (every item has a status) and `notes` (the traveler's own general note, unrelated to
     * whether anything's booked — including it here used to make an unbooked item with nothing but a
     * private note show a misleading "View booking details" affordance).
     */
    get hasBookingInfo(): boolean {
      return (
        Boolean(this.booking?.confirmationNumber || this.booking?.customerRequest) ||
        (this.attachments?.length ?? 0) > 0
      );
    },

    /** Whether "View booking details" should show: booked, approved, or has manually-recorded info. */
    get canViewBooking(): boolean {
      return this.isBooked || this.isApproved || this.hasBookingInfo;
    },

    /**
     * Whether "Add booking" should show: not cancelled, not already booked/approved, and nothing
     * recorded yet. `updateBookingDetails` sets `booking.status: 'booked'` unconditionally on save —
     * even with no confirmation number, cost, or attachment entered — so `!this.hasBookingInfo` alone
     * isn't enough to catch that case; without the `isBooked`/`isApproved` check, saving an empty
     * booking left "Add booking" visible forever afterward, with no way for it to ever hide.
     */
    get canAddBooking(): boolean {
      return !this.isCancelled && !this.isBooked && !this.isApproved && !this.hasBookingInfo;
    },

    /**
     * Whether "Edit booking" should show: manual booking entry is for externally-managed items
     * only — never for a cancelled booking, never for a GDS-automated one (Sabre or Travelport) (those change through
     * cancel/rebook, not in-place edits), and never for a room linked to a priced rate (those book
     * exclusively through checkout — ELARA-870).
     */
    get canEditBooking(): boolean {
      return (
        !this.isCancelled &&
        !isAutomatedBookingChannel(this.booking?.channel) &&
        !(this.type === 'room' && this.referenceId)
      );
    },

    /**
     * Whether "Remove booking" should show: the same manual-entry gate as `canEditBooking`, plus
     * something actually recorded to remove. A GDS booking is deliberately excluded — it's a real
     * PNR with its own cancellation flow, not an entry to wipe.
     */
    get canRemoveBooking(): boolean {
      return this.canEditBooking && this.canViewBooking;
    },

    /**
     * Whether "Remove booking" should remove the ITEM, not just clear its `booking` map. An
     * external stay or flight (no `referenceId`) exists ONLY as the record of a booking made
     * outside Elara — there is no searched rate or flight option behind it to rebook, so clearing
     * the booking would strand a "Not booked" shell whose Book action has nothing to check out.
     * Plan-first items (a searched flight, a place, an activity) keep their identity without a
     * booking and only get their entered details cleared.
     *
     * Rooms additionally require the POSITIVE `channel: 'manual'` marker, mirroring
     * `DeleteItineraryItemFlow`'s fail-closed guard exactly: a booked room whose record is
     * ambiguous (legacy entry with no channel) would be REJECTED by the server delete, so it
     * degrades to the safe clear-only path here instead of surfacing an error.
     */
    get removingBookingRemovesItem(): boolean {
      if (this.referenceId) return false;
      if (this.type === 'flight') return true;
      return this.type === 'room' && this.booking?.channel === 'manual';
    },

    /** Rooms become Elara-fulfilled once booked/approved and must be cancelled before removal; every other item type stays user-removable. */
    get isItemRemovable(): boolean {
      return this.type !== 'room' || (!this.isBooked && !this.isApproved);
    },

    /**
     * Lazily hydrate the related flight option from Firestore by referenceId.
     * Returns undefined until the document is ready or on error.
     */
    get flightOption(): FlightOptionModel | undefined {
      if (this.type !== 'flight') return undefined;
      const tripId = tripIdFromPath(this.path);
      const refId = this.referenceId;
      if (!tripId || !refId) return undefined;
      const handle = FlightOptionModel.get({ tripId, id: refId });
      if (handle.isReady && handle.data) return handle.data;
      if (handle.error) {
        console.warn(`[ItineraryItem] Failed to load flight option ${refId}:`, handle.error);
      }
      return undefined;
    },

    /**
     * A flight booked outside Elara carries its details on the item itself, since there is no
     * FlightOption document to hydrate. Undefined unless the snapshot really holds legs.
     */
    get externalFlight(): FlightOption | undefined {
      if (this.type !== 'flight') return undefined;
      const snapshot = this.referenceData as FlightOption | null | undefined;
      if (snapshot == null || typeof snapshot !== 'object') return undefined;
      const hasLegs = (snapshot.outboundSegments?.length ?? 0) > 0 || (snapshot.segments?.length ?? 0) > 0;
      return hasLegs ? snapshot : undefined;
    },

    /**
     * Lazily hydrate the related room option (referenceId = RoomOption doc id).
     * Used for type hotel and type room itinerary items.
     */
    get roomOption(): RoomOptionModel | undefined {
      if (this.type !== 'room') return undefined;
      const tripId = tripIdFromPath(this.path);
      const refId = this.referenceId;
      if (!tripId || !refId) return undefined;
      const handle = RoomOptionModel.get({ tripId, id: refId });
      if (handle.isReady && handle.data) return handle.data;
      if (handle.error) {
        console.warn(`[ItineraryItem] Failed to load room option ${refId}:`, handle.error);
      }
      return undefined;
    },

    /**
     * Lazily hydrate the related hotel.
     * For room items: prefer the linked RoomOption's hotel (Elara-sourced rooms),
     * then fall back to `referenceData.hotelId` for externally-booked rooms that
     * carry no RoomOption but were matched to a catalog hotel (e.g. an Amex/Chase
     * booking saved via the concierge). Stays byte-identical for RoomOption-backed
     * rooms — the fallback is only reached when there is no RoomOption hotel.
     */
    get hotel(): Hotel | undefined {
      if (this.type !== 'room') return undefined;

      const fromRoomOption = this.roomOption?.hotel;
      if (fromRoomOption) return fromRoomOption;

      // `referenceData` is an arbitrary record (unknown values), so the id is cast.
      const refData = this.referenceData as { hotelId?: string } | null | undefined;
      const hotelId = parseDocumentId(refData?.hotelId);
      if (!hotelId) return undefined;

      const handle = HotelModel.get({ id: hotelId });
      if (handle.error) {
        console.warn(`[ItineraryItem] Failed to load hotel ${hotelId}:`, handle.error);
        return undefined;
      }
      return handle.data;
    },

    /**
     * Display name for a room item's hotel. Resolves the matched/linked Hotel doc
     * first, then the denormalized `referenceData.hotelName` snapshot for external
     * bookings with no catalog match. Returns undefined for non-room items and when
     * no name is available, so call sites apply their own literal fallback.
     */
    get hotelName(): string | undefined {
      if (this.type !== 'room') return undefined;
      const refData = this.referenceData as { hotelName?: string } | null | undefined;
      const fromReferenceData = typeof refData?.hotelName === 'string' ? refData.hotelName : undefined;
      return this.hotel?.name ?? fromReferenceData;
    },

    /**
     * Lazily hydrate the related place from Firestore by referenceId (place document id).
     * Returns undefined until the document is ready or on error.
     */
    get place(): Place | undefined {
      if (this.type !== 'place') return undefined;
      const placeId = parseDocumentId(this.referenceId);
      if (!placeId) return undefined;
      const handle = PlaceModel.get({ id: placeId });
      if (handle.error) {
        console.warn(`[ItineraryItem] Failed to load place ${placeId}:`, handle.error);
        return undefined;
      }
      return hydratedFromHandle(handle) as Place | undefined;
    },

    /**
     * For an externally-booked room with no catalog hotel match, the concierge may attach a Google
     * Place (`referenceData.placeId` → `places/{id}`) so the stay still gets a map pin, address, and
     * photo. Resolves that Place; undefined for non-room items or when no `placeId` is present.
     */
    get externalPlace(): Place | undefined {
      if (this.type !== 'room') return undefined;
      const refData = this.referenceData as { placeId?: unknown } | null | undefined;
      const placeId = typeof refData?.placeId === 'string' ? parseDocumentId(refData.placeId) : undefined;
      if (!placeId) return undefined;
      const handle = PlaceModel.get({ id: placeId });
      if (handle.error) {
        console.warn(`[ItineraryItem] Failed to load external place ${placeId}:`, handle.error);
        return undefined;
      }
      return hydratedFromHandle(handle) as Place | undefined;
    },

    /**
     * True once every lazily-hydrated reference this item needs for display and
     * destination grouping has resolved — the referenced doc loaded, errored, or
     * is absent. Lets the itinerary builder hold its render until groupings are
     * stable, instead of reshuffling as hotels/flights/places stream in (ELARA-836).
     */
    get referencesReady(): boolean {
      const refId = this.referenceId;
      const tripId = tripIdFromPath(this.path);
      switch (this.type) {
        case 'flight': {
          if (!refId || !tripId) return true;
          const handle = FlightOptionModel.get({ tripId, id: refId });
          return handle.isReady || handle.error != null;
        }
        case 'room': {
          if (!refId || !tripId) return true;
          const handle = RoomOptionModel.get({ tripId, id: refId });
          if (handle.error != null) return true;
          if (!handle.isReady) return false;
          // RoomOption resolved; the hotel (drives the destination label) must resolve too.
          return handle.data?.hotelReady ?? true;
        }
        case 'place': {
          const placeId = parseDocumentId(refId);
          if (!placeId) return true;
          const handle = PlaceModel.get({ id: placeId });
          return handle.isReady || handle.error != null;
        }
        default:
          return true;
      }
    },

    get cost(): Cost | undefined {
      // A recorded booking price always wins over a live search estimate, for every item type: the
      // frozen Elara-checkout price first, else a manually-entered actual cost (booked directly with
      // the provider, then added here) — either way, once there's an actual/agreed price, that's the
      // truth, not the estimate, which can drift or vanish (rate expires, search result ages out)
      // after booking. Without this, a manually-recorded price never showed for a flight, and a
      // booked room with a catalog match kept recomputing its live rate underneath the agreed price.
      const recordedCost = this.booking?.priceSnapshot?.totalCost ?? this.booking?.actualCost;
      if (recordedCost) return recordedCost;

      if (this.type === 'flight') return this.flightOption?.cost ?? this.externalFlight?.cost;
      if (this.type === 'room') {
        const roomOption = this.roomOption;
        if (!roomOption) return undefined; // External room, nothing recorded yet — no live rate to show.
        // Identity is `(productCode, occupancy)`. Legacy items may still carry only
        // `selectedRateKey`; fall back to it so existing data keeps rendering until
        // the migration backfills.
        const meta = this.metadata as (RoomSelection & { selectedRateKey?: string }) | undefined;
        // Each itinerary item represents one room; multi-room bookings use multiple items.
        if (meta?.productCode) {
          return roomOption.totalCost(1, { productCode: meta.productCode, occupancy: meta.occupancy });
        }
        if (meta?.selectedRateKey) {
          return roomOption.totalCost(1, meta.selectedRateKey);
        }
        return roomOption.totalCost(1);
      }
      return undefined;
    },

    /**
     * This room item's matched rate — resolved from the linked `RoomOption` + selection metadata
     * (`productCode`/`occupancy` — the current `RoomSelectionSchema`; no legacy `selectedRateKey`
     * fallback, since item metadata is never written with one post rate-identity migration). Undefined
     * for non-room items, externally-booked rooms with no catalog match, or once the rate falls out of
     * the room's live search results (sold out, cache expired) — `cancellationDeadline`/
     * `cancellationPolicyText` below fall back to the frozen booking data in that case instead of
     * going blank.
     */
    get rate(): RoomRateModel | undefined {
      if (this.type !== 'room') return undefined;
      return resolveRateFromMetadata(this.roomOption, this.metadata as RoomSelection | undefined);
    },

    /**
     * The resolved free-cancellation deadline for this room stay — the booking's frozen policy once
     * booked (so it can't drift from, or vanish alongside, the live rate), else the live rate's.
     * Dates prefer the confirmed (booked) stay dates over the item's mutable ones (ELARA-867).
     * Undefined for non-room items or until a structured policy and both stay dates are available.
     *
     * The single source every cancellation-fact surface (booking-details sheet/dialog, itinerary
     * event detail, room guide) should read from — recomputing this independently is what caused
     * the same booking to show different cancellation text/color across screens.
     */
    get cancellationDeadline(): CancellationDeadline | undefined {
      const cancellationPolicies = this.booking?.cancellationPolicies ?? this.rate?.cancellationPolicies;
      if (!cancellationPolicies?.length || !this.startDateTime || !this.endDateTime) return undefined;
      const stayDates = this.confirmedStayDates;
      return resolveCancellationDeadline({
        cancellationPolicies,
        checkInDate: stayDates?.checkInDate ?? this.startDateTime,
        checkOutDate: stayDates?.checkOutDate ?? this.endDateTime,
      });
    },

    /** "Free cancellation until Mar 12, 2026" / "Non-refundable" from `cancellationDeadline`. */
    get cancellationDeadlineLabel(): string | undefined {
      return formatCancellationDeadline(this.cancellationDeadline);
    },

    /**
     * Whether cancellation is CURRENTLY free — accounts for the free-cancellation window having
     * already passed, unlike the rate's static `isRefundable` flag. Falls back to that flag only
     * pre-booking, when no deadline can resolve yet (no confirmed dates/policies).
     */
    get isFreeCancellation(): boolean {
      const deadline = this.cancellationDeadline;
      if (deadline) {
        return deadline.refundable === true && (!deadline.freeUntil || deadline.freeUntil.getTime() >= Date.now());
      }
      return this.rate?.isRefundable ?? false;
    },

    /**
     * Whether the traveler may cancel this booking themselves: a live, GDS-automated room booking
     * (Sabre or Travelport) that is still inside its free-cancellation window. Once cancelling would
     * forfeit the payment, the concierge team handles it and cancels with the hotel directly. Unknown
     * terms are not free (see `isFreeCancellation`).
     */
    get canSelfCancelBooking(): boolean {
      return this.isLiveAutomatedRoomBooking && this.isFreeCancellation;
    },

    /** A live automated booking whose cancellation is no longer free — cancel through the team, not in-app. */
    get cancellingForfeitsPayment(): boolean {
      return this.isLiveAutomatedRoomBooking && !this.isFreeCancellation;
    },

    /** A booked, not-cancelled room that Elara created at a GDS — the only kind with a reservation to cancel. */
    get isLiveAutomatedRoomBooking(): boolean {
      return (
        this.type === 'room' && this.isBooked && !this.isCancelled && isAutomatedBookingChannel(this.booking?.channel)
      );
    },

    /**
     * The human-readable cancellation policy description shown as the secondary/detail text
     * alongside `cancellationDeadlineLabel`. Built off `describeCancellationPolicyFacts` — the same
     * BINDING-first logic hotel checkout uses (`views/checkout/policy-utils.ts`): the deterministic
     * structured terms first, the generated prose only as a fallback (never the primary text — an LLM
     * can soften or omit a penalty, ELARA-829 R1), then a bare refundable/non-refundable statement.
     * Prefers the booking's own frozen policies/prose (captured at submit) over the live rate's, so it
     * can't drift once the rate changes or falls out of search results.
     */
    get cancellationPolicyText(): string | undefined {
      const stayDates = this.confirmedStayDates;
      return describeCancellationPolicyFacts(
        {
          cancellationPolicies: this.booking?.cancellationPolicies ?? this.rate?.cancellationPolicies,
          generatedCancellationPolicy: this.booking?.cancellationPolicy ?? this.rate?.generated?.cancellationPolicy,
          refundable: this.rate?.refundable,
        },
        stayDates?.checkInDate ?? this.startDateTime,
      );
    },

    get mapPoint(): MapPoint | undefined {
      if (this.type === 'room') {
        // Catalog hotel first (matched), else the attached Google Place (unmatched external).
        return this.hotel?.mapPoint ?? this.externalPlace?.mapPoint;
      }
      if (this.type === 'place' && this.place) {
        return this.place.mapPoint;
      }
      return undefined;
    },

    // --- Stay display fields ---------------------------------------------------------------
    // A room stay's descriptive/contact/guide fields resolve the catalog hotel first, then fall
    // back to the attached Google Place for an externally-booked stay with no catalog match. This
    // lets the itinerary/booking UI read one seam instead of branching on hotel-vs-place, so a
    // Places-backed external stay renders the same description/contact/directions/guide a catalog
    // hotel does. All return undefined for non-room items.

    /**
     * Long-form description for a room stay: the catalog hotel's long description, else the
     * externally-booked stay's Google-Place AI description — which is populated lazily when the
     * place guide is first opened, so it may be absent until then.
     */
    get stayDescription(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.long_description ?? this.externalPlace?.generated?.description ?? undefined;
    },

    /** Short-form description for a room stay; falls back to the Google-Place description. */
    get stayShortDescription(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.short_description ?? this.externalPlace?.generated?.description ?? undefined;
    },

    /** Contact phone for a room stay — catalog hotel first, then the matched Google Place. */
    get stayPhone(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.contact?.phone ?? this.externalPlace?.contactLinks.phone ?? undefined;
    },

    /** Website for a room stay — catalog hotel first, then the matched Google Place. */
    get stayWebsite(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.website ?? this.externalPlace?.contactLinks.website ?? undefined;
    },

    /**
     * Images for a room stay — the catalog hotel's preview images, else the attached Google Place's
     * photos for an externally-booked stay with no catalog match. Empty for non-room items.
     */
    get stayImages(): WidgetImage[] {
      if (this.type !== 'room') return [];
      return this.hotel?.previewImages ?? this.externalPlace?.allImages ?? [];
    },

    /**
     * The one image that represents this item on a thumbnail / cover surface: for a room, the booked
     * room's own photo first, then the stay's image (catalog hotel, else the attached Google Place —
     * so an externally-booked stay matched only to a Place still shows a picture); for a place item,
     * the place's photo. Undefined for flights (they render an airline logo instead) and for items
     * with no imagery, so call sites fall back to their icon.
     */
    get coverImageUrl(): string | undefined {
      return this.coverImageUrls[0];
    },

    /**
     * Every image that could represent this item, best first — the same order {@link coverImageUrl}
     * picks from, kept as a list so a surface can fall through to the next when one fails to load.
     *
     * Supplier photo CDNs rot (a third of the urls on some stored rooms 404), and a thumbnail has no
     * room for a gallery to page past a dead one, so a booked stay showed a broken glyph while the same
     * room rendered fine wherever a carousel happened to exist. The room's own photos come first and
     * the stay's follow rather than only standing in for them, so a room whose every photo has gone
     * still shows its hotel. Pass to an image seam that takes a candidate list — `AppImage`'s array
     * `src` on mobile, `useFirstAvailableImage` on web.
     */
    get coverImageUrls(): string[] {
      if (this.type === 'room') {
        return [...(this.roomOption?.images ?? []), ...this.stayImages].map((image) => image.url).filter(Boolean);
      }
      if (this.type === 'place') {
        // `primaryImage` leads because it is what this surface has always shown; `allImages` are the
        // fallbacks behind it.
        const rest = (this.place?.allImages ?? []).map((image) => image.url);
        return [this.place?.primaryImage, ...rest].filter((url): url is string => !!url);
      }
      return [];
    },

    /** Short address for a room stay — catalog hotel first, then the matched Google Place. */
    get stayAddress(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.shortAddress ?? this.externalPlace?.shortAddress ?? undefined;
    },

    /** Directions URL for a room stay — catalog hotel map URL, then the Google-Place directions link. */
    get stayDirectionsUrl(): string | undefined {
      if (this.type !== 'room') return undefined;
      return this.hotel?.mapUrl ?? this.externalPlace?.googleMapsLinks?.directionsUri ?? undefined;
    },

    /**
     * Reactive room-type label for a room stay: the RoomOption title for Elara-booked rooms, else
     * the denormalized `referenceData.roomName` for an externally-booked stay. Falls back to the
     * persisted title, then "Room". Unlike the persisted `title`/`displayTitle` (denormalized at add
     * time), this resolves live from `referenceData`, so an external stay added before the server
     * title fix still shows its room name on surfaces that read it (e.g. the bookings row).
     */
    get roomLabel(): string | undefined {
      if (this.type !== 'room') return undefined;
      const fromRoomOption = this.roomOption?.displayName;
      if (fromRoomOption) return fromRoomOption;
      const refData = this.referenceData as { roomName?: unknown } | null | undefined;
      const roomName = typeof refData?.roomName === 'string' ? refData.roomName.trim() : undefined;
      if (roomName) return roomName;
      const persisted = typeof this.title === 'string' ? this.title.trim() : '';
      return persisted !== '' ? persisted : 'Room';
    },

    /**
     * Where the "view guide" affordance should point for a room stay: the catalog hotel guide when
     * matched, else the Google-Place guide for an external stay. Undefined when neither resolves, so
     * call sites hide the affordance.
     */
    get guideRef(): { kind: 'hotel' | 'place'; id: string } | undefined {
      if (this.type !== 'room') return undefined;
      const refData = this.referenceData as { hotelId?: unknown; placeId?: unknown } | null | undefined;
      // Resolve the hotel id without waiting on the hotel doc itself — the linked RoomOption and the
      // denormalized `referenceData.hotelId` both carry it, so the guide link is stable from first
      // render instead of briefly falling through to the place while the hotel loads.
      const hotelId =
        this.roomOption?.hotelId ??
        (typeof refData?.hotelId === 'string' ? parseDocumentId(refData.hotelId) : undefined) ??
        this.hotel?.id;
      if (hotelId) return { kind: 'hotel', id: hotelId };
      const placeId = typeof refData?.placeId === 'string' ? parseDocumentId(refData.placeId) : undefined;
      if (placeId) return { kind: 'place', id: placeId };
      return undefined;
    },

    /** Expands this itinerary item into display events */
    expandToEvents(): ItineraryItemEvent[] {
      return expandSingleItineraryItem(this);
    },
  }));

  return model;
}

export type ClientItineraryItemModel = ReturnType<typeof createClientItineraryItemModel>;
export type ItineraryItem = ClientInstanceOf<ClientItineraryItemModel>;
